const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-10-15';

/**
 * Reads the Klaviyo public key from the onsite script Klaviyo's app embed injects.
 * Used as a fallback when the block setting is empty.
 */
function detectCompanyId() {
  const script = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
  const match = script?.getAttribute('src')?.match(/\/onsite\/js\/([^/]+)\/klaviyo\.js/);
  return match ? match[1] : null;
}

function buildPayload(email, variantId) {
  return {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: ['EMAIL'],
        profile: { data: { type: 'profile', attributes: { email } } },
      },
      relationships: {
        variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
      },
    },
  };
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define(
    'back-in-stock-form',
    class BackInStockForm extends HTMLElement {
      connectedCallback() {
        this.addEventListener('submit', this.handleSubmit);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.updateVariant);
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.handleSubmit);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      get sectionId() {
        const productInfo = this.closest('product-info');
        return productInfo?.dataset.originalSection || productInfo?.dataset.section || this.dataset.section;
      }

      /**
       * Swaps the form for the re-rendered one when the selected variant changes.
       */
      updateVariant = (event) => {
        if (event.data.sectionId !== this.sectionId) return;
        const updated = event.data.html.getElementById(this.id);
        if (!updated) return;
        this.innerHTML = updated.innerHTML;
      };

      handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const input = form.querySelector('input[type="email"]');
        const button = form.querySelector('button[type="submit"]');
        if (!(input instanceof HTMLInputElement) || !(button instanceof HTMLButtonElement)) return;

        if (!input.checkValidity()) {
          input.reportValidity();
          return;
        }

        const companyId = this.dataset.companyId || detectCompanyId();
        const variantId = form.dataset.variantId;

        if (!companyId || !variantId) {
          console.error('[back-in-stock] Missing Klaviyo public key or variant id');
          this.setState(form, 'error');
          return;
        }

        button.disabled = true;
        this.setState(form, 'pending');

        try {
          const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
            body: JSON.stringify(buildPayload(input.value.trim(), variantId)),
          });

          if (!response.ok) {
            throw new Error(`Klaviyo responded with ${response.status}`);
          }

          this.setState(form, 'success');
        } catch (error) {
          console.error('[back-in-stock] Subscription failed', error);
          this.setState(form, 'error');
        } finally {
          button.disabled = false;
        }
      };

      setState(form, state) {
        form.dataset.state = state;
        form.querySelectorAll('.back-in-stock__message').forEach((message) => {
          message.hidden = !message.classList.contains(`back-in-stock__message--${state}`);
        });
      }
    }
  );
}
